import React from "react";
import Modal from "components/ui/Modal";
import DataTablePagination from "components/ui/DataTablePagination";
import { toastError, toastSuccess } from "lib/toast";
import { formatLocaleDateTime } from "lib/localeFormat";
import { adminService } from "services/adminService";
import type {
  AdminStudentDevice,
  AdminStudentDevicesPayload,
  AdminStudentLoginLog,
} from "services/adminStudentDevicesService";

type Props = {
  open: boolean;
  onClose: () => void;
  studentId: number | null;
  studentName: string;
  isArabic: boolean;
};

const statusLabels: Record<string, [string, string]> = {
  success: ["نجاح", "Success"],
  rejected_device_limit: ["رفض — حد الأجهزة", "Rejected — device limit"],
  failed_credentials: ["فشل بيانات الدخول", "Failed credentials"],
  failed_inactive: ["حساب غير نشط", "Inactive account"],
};

function statusLabel(status: string, isArabic: boolean): string {
  const labels = statusLabels[status];
  return labels ? (isArabic ? labels[0] : labels[1]) : status || "—";
}

function formatDate(value: string | null | undefined, isArabic: boolean): string {
  return formatLocaleDateTime(value, isArabic, { dateStyle: "medium", timeStyle: "short" });
}

export default function StudentDevicesModal({
  open,
  onClose,
  studentId,
  studentName,
  isArabic,
}: Props) {
  const [overview, setOverview] = React.useState<AdminStudentDevicesPayload | null>(null);
  const [logs, setLogs] = React.useState<AdminStudentLoginLog[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [removingId, setRemovingId] = React.useState<number | null>(null);
  const [page, setPage] = React.useState(1);
  const [lastPage, setLastPage] = React.useState(1);
  const [total, setTotal] = React.useState(0);
  const [range, setRange] = React.useState({ from: 0, to: 0 });
  const [statusFilter, setStatusFilter] = React.useState("");
  const [section, setSection] = React.useState<"devices" | "logs">("devices");

  const load = React.useCallback(async () => {
    if (studentId === null) return;
    setLoading(true);
    try {
      const [devicesResult, logsResult] = await Promise.all([
        adminService.getStudentDevices(studentId),
        adminService.getStudentLoginLogs(studentId, {
          page,
          limit: 15,
          status: statusFilter || undefined,
        }),
      ]);
      setOverview(devicesResult);
      setLogs(logsResult.data || []);
      setLastPage(Math.max(1, Number(logsResult.meta?.last_page) || 1));
      setTotal(Number(logsResult.meta?.total) || 0);
      setRange({
        from: Number(logsResult.meta?.from) || 0,
        to: Number(logsResult.meta?.to) || 0,
      });
    } catch (error) {
      console.error("Failed to load student devices:", error);
      toastError(isArabic ? "تعذر تحميل أجهزة الطالب." : "Could not load student devices.");
    } finally {
      setLoading(false);
    }
  }, [studentId, isArabic, page, statusFilter]);

  React.useEffect(() => {
    if (open) void load();
    else {
      setOverview(null);
      setLogs([]);
      setSection("devices");
      setStatusFilter("");
      setPage(1);
      setRemovingId(null);
    }
  }, [open, load]);

  React.useEffect(() => {
    if (open) setPage(1);
  }, [open, statusFilter]);

  const removeDevice = async (device: AdminStudentDevice) => {
    if (studentId === null) return;
    const label = device.device_name || device.device_id;
    const confirmed = window.confirm(
      isArabic
        ? `حذف الجهاز «${label}»؟ سيتمكن الطالب من تسجيل الدخول من جهاز جديد.`
        : `Remove device “${label}”? The student can then sign in on a new device.`
    );
    if (!confirmed) return;

    setRemovingId(device.id);
    try {
      const next = await adminService.removeStudentDevice(studentId, device.id);
      setOverview(next);
      toastSuccess(isArabic ? "تم حذف الجهاز." : "Device removed.");
      await load();
    } catch (error: any) {
      const message = error?.response?.data?.message || error?.response?.data?.result?.message;
      toastError(message || (isArabic ? "تعذر حذف الجهاز." : "Could not remove device."));
    } finally {
      setRemovingId(null);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="xl"
      title={isArabic ? "أجهزة الطالب: " + studentName : "Student devices: " + studentName}
      footer={
        <button
          type="button"
          onClick={onClose}
          className="rounded-lg bg-gray-100 px-4 py-2 text-sm font-semibold text-gray-700 dark:bg-white/10 dark:text-white"
        >
          {isArabic ? "إغلاق" : "Close"}
        </button>
      }
    >
      {loading && !overview ? (
        <p className="py-10 text-center text-sm text-gray-500">
          {isArabic ? "جارٍ التحميل..." : "Loading..."}
        </p>
      ) : (
        <div className="space-y-6" dir={isArabic ? "rtl" : "ltr"}>
          <div className="grid gap-3 sm:grid-cols-3">
            <div className="rounded-xl border border-gray-200 p-3 dark:border-white/10">
              <p className="text-xs text-gray-500">{isArabic ? "الحد الأقصى" : "Max devices"}</p>
              <p className="mt-1 text-lg font-bold text-navy-700 dark:text-white">
                {overview?.max_devices ?? "—"}
              </p>
            </div>
            <div className="rounded-xl border border-gray-200 p-3 dark:border-white/10">
              <p className="text-xs text-gray-500">{isArabic ? "مستخدم" : "Used"}</p>
              <p className="mt-1 text-lg font-bold text-orange-600">{overview?.used_slots ?? "—"}</p>
            </div>
            <div className="rounded-xl border border-gray-200 p-3 dark:border-white/10">
              <p className="text-xs text-gray-500">{isArabic ? "متبقي" : "Remaining"}</p>
              <p className="mt-1 text-lg font-bold text-emerald-600">
                {overview?.remaining_slots ?? "—"}
              </p>
            </div>
          </div>

          <div className={`flex gap-2 ${isArabic ? "flex-row-reverse" : ""}`}>
            <button
              type="button"
              onClick={() => setSection("devices")}
              className={
                "rounded-lg px-3 py-1.5 text-sm font-semibold " +
                (section === "devices"
                  ? "bg-brand-500 text-white"
                  : "bg-gray-100 text-gray-700 dark:bg-white/10 dark:text-white")
              }
            >
              {isArabic ? "الأجهزة" : "Devices"}
            </button>
            <button
              type="button"
              onClick={() => setSection("logs")}
              className={
                "rounded-lg px-3 py-1.5 text-sm font-semibold " +
                (section === "logs"
                  ? "bg-brand-500 text-white"
                  : "bg-gray-100 text-gray-700 dark:bg-white/10 dark:text-white")
              }
            >
              {isArabic ? "سجل الدخول" : "Login history"}
            </button>
          </div>

          {section === "devices" ? (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[640px] text-sm">
                <thead>
                  <tr className="border-b border-gray-200 text-left dark:border-white/10">
                    <th className="px-2 py-2 font-semibold text-gray-600 dark:text-white">
                      {isArabic ? "الاسم" : "Name"}
                    </th>
                    <th className="px-2 py-2 font-semibold text-gray-600 dark:text-white">
                      {isArabic ? "المنصة" : "Platform"}
                    </th>
                    <th className="px-2 py-2 font-semibold text-gray-600 dark:text-white">
                      {isArabic ? "آخر دخول" : "Last login"}
                    </th>
                    <th className="px-2 py-2 font-semibold text-gray-600 dark:text-white">IP</th>
                    <th className="px-2 py-2 font-semibold text-gray-600 dark:text-white">
                      {isArabic ? "إجراء" : "Action"}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {(overview?.devices || []).length === 0 ? (
                    <tr>
                      <td colSpan={5} className="px-2 py-8 text-center text-gray-500">
                        {isArabic ? "لا توجد أجهزة مسجّلة." : "No registered devices."}
                      </td>
                    </tr>
                  ) : (
                    (overview?.devices || []).map((device) => (
                      <tr key={device.id} className="border-b border-gray-100 dark:border-white/5">
                        <td className="px-2 py-3">
                          <div className="font-medium text-navy-700 dark:text-white">
                            {device.device_name || "—"}
                          </div>
                          <div className="mt-0.5 max-w-[220px] truncate text-xs text-gray-400">
                            {device.device_id}
                          </div>
                        </td>
                        <td className="px-2 py-3 uppercase text-gray-600 dark:text-white">
                          {device.platform}
                        </td>
                        <td className="px-2 py-3 text-gray-600 dark:text-white">
                          {formatDate(device.last_login_at, isArabic)}
                        </td>
                        <td className="px-2 py-3 text-gray-600 dark:text-white">
                          {device.last_ip || "—"}
                        </td>
                        <td className="px-2 py-3">
                          <button
                            type="button"
                            disabled={removingId === device.id}
                            onClick={() => void removeDevice(device)}
                            className="rounded-lg bg-red-50 px-3 py-1.5 text-xs font-semibold text-red-600 hover:bg-red-100 disabled:opacity-50 dark:bg-red-500/10"
                          >
                            {removingId === device.id
                              ? isArabic
                                ? "جارٍ الحذف..."
                                : "Removing..."
                              : isArabic
                                ? "حذف"
                                : "Remove"}
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="space-y-3">
              <div className={`flex items-center gap-2 ${isArabic ? "flex-row-reverse" : ""}`}>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm dark:border-white/10 dark:bg-navy-800 dark:text-white"
                >
                  <option value="">{isArabic ? "كل الحالات" : "All statuses"}</option>
                  <option value="success">{isArabic ? "نجاح" : "Success"}</option>
                  <option value="rejected_device_limit">
                    {isArabic ? "رفض — حد الأجهزة" : "Rejected — device limit"}
                  </option>
                  <option value="failed_credentials">
                    {isArabic ? "فشل بيانات الدخول" : "Failed credentials"}
                  </option>
                  <option value="failed_inactive">
                    {isArabic ? "حساب غير نشط" : "Inactive"}
                  </option>
                </select>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full min-w-[720px] text-sm">
                  <thead>
                    <tr className="border-b border-gray-200 text-left dark:border-white/10">
                      <th className="px-2 py-2 font-semibold text-gray-600 dark:text-white">
                        {isArabic ? "الوقت" : "When"}
                      </th>
                      <th className="px-2 py-2 font-semibold text-gray-600 dark:text-white">
                        {isArabic ? "الحالة" : "Status"}
                      </th>
                      <th className="px-2 py-2 font-semibold text-gray-600 dark:text-white">
                        {isArabic ? "الجهاز" : "Device"}
                      </th>
                      <th className="px-2 py-2 font-semibold text-gray-600 dark:text-white">IP</th>
                      <th className="px-2 py-2 font-semibold text-gray-600 dark:text-white">
                        {isArabic ? "ملاحظة" : "Message"}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {logs.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="px-2 py-8 text-center text-gray-500">
                          {isArabic ? "لا يوجد سجل دخول." : "No login history."}
                        </td>
                      </tr>
                    ) : (
                      logs.map((log) => (
                        <tr key={log.id} className="border-b border-gray-100 dark:border-white/5">
                          <td className="px-2 py-3 text-gray-600 dark:text-white">
                            {formatDate(log.created_at, isArabic)}
                          </td>
                          <td className="px-2 py-3 text-gray-700 dark:text-white">
                            {statusLabel(log.status, isArabic)}
                          </td>
                          <td className="px-2 py-3">
                            <div className="text-gray-700 dark:text-white">
                              {log.device_name || log.platform || "—"}
                            </div>
                            {log.device_id ? (
                              <div className="mt-0.5 max-w-[180px] truncate text-xs text-gray-400">
                                {log.device_id}
                              </div>
                            ) : null}
                          </td>
                          <td className="px-2 py-3 text-gray-600 dark:text-white">
                            {log.ip || "—"}
                          </td>
                          <td className="px-2 py-3 text-gray-500">{log.message || "—"}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              <DataTablePagination
                isArabic={isArabic}
                page={page}
                totalPages={lastPage}
                fromItem={range.from}
                toItem={range.to}
                totalItems={total}
                onPageChange={setPage}
              />
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}
